import { cloneDeep } from "lodash";
import * as luxon from "luxon";
import React, { createContext, ReactNode, useEffect, useState } from "react";
import Confetti from "react-confetti";
import FadeIn from "../../../../src/vendored/FadeIn";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import BeatLoader from "react-spinners/BeatLoader";
import ClipLoader from "react-spinners/ClipLoader";
import { common_format_utils } from "../../../../common/common_format_utils";
import { common_utils } from "../../../../common/common_utils";
import { Constants } from "../../../../common/constants";
import {
	AttendeeRoleDataWithIDs,
	ChannelOverrideSettingsSetRequest,
	CommandPermissions,
	DiscordAuthToken,
	GetServerSettingsRequest,
	GuildChannelSettingsOverrides,
	GuildEntityColumns,
	GuildSettings,
	GuildSettingsProps,
	GuildSettingsSectionNames,
	GuildSettingsSectionType,
	GuildSettingsSetRequest,
	ModifyGuildActivationRequest,
	ModifyPremiumStatusWebResponse,
	NativeEventData,
	OptionPreset,
	ResetCalendarFeedsRequest,
	SeshTextChannelData,
	SetGuildPermissionsRequest,
	SetGuildPresetRequest,
	SetGuildTimezoneRequest,
	SetSecondaryTimezoneRequest,
	SignUpPresetsProp,
} from "../../../../common/sesh_types";
import {
	getSettingForChannel,
	guildSettingsStyle_getIndividualChannelSettingOverride,
	guildSettingsStyle_setIndividualChannelSettingOverride,
	hasActiveChannelOverrideForSetting,
} from "../../../../common/type_utils";
import { DashboardUtils, FADE_IN_ANIMATION_DURATION } from "../dashboard_utils";
import ColorPickerAccordian from "../forms/accordians/color_picker_accordian";
import { AddRoleToAttendeesComponent } from "../forms/improved_components/add_role_to_attendees_component";
import { ModifySettingsButton } from "../forms/improved_components/base_components/modify_settings_button";
import { Tag } from "../forms/improved_components/base_components/tag";
import { ChannelSelectFormWithTags } from "../forms/improved_components/channel_dropdown_with_tags";
import { ErrorNote } from "../forms/improved_components/error_note";
import { EventThreadAccordian } from "../forms/improved_components/event_thread_form";
import { useCurrentGuildData } from "../forms/improved_components/hooks/use_guild_data";
import { useIsMobile } from "../forms/improved_components/hooks/use_is_mobile";
import { InlineCode } from "../forms/improved_components/inline_code";
import { RSVPOptionsTable } from "../forms/improved_components/rsvp_options_table";
import { GuildLogsBlurb } from "../forms/improved_components/guild_logs/guild_log_blurb";
import { GuildLogsPage } from "../forms/improved_components/guild_logs/guild_log_page";
import { ServerRemindersForm } from "../forms/improved_components/server_reminders/server_reminders_blurb";
import { InfoTooltip } from "../forms/improved_components/tooltips/info_tooltip";
import DiscordNativeEventForm from "../forms/native_event_form";
import { PermissionsForm } from "../forms/permissions_form";
import RSVPOptionsForm from "../forms/rsvp_options";
import SeshAccordian from "../forms/sesh_accordian";
import { TimezonePickerForm } from "../forms/timezone_picker";
import { setDocTitle } from "../web_ui";
import { AISettingsPage } from "./ai_settings/ai_settings_page";
import SyncSettings from "./sync_settings/sync_settings";
import { EventTemplateBlurb } from "./event_template_blurb";
import {
	EventTemplateContents,
	EventTemplatePage,
	EventTemplatesHeader,
} from "../forms/improved_components/event_templates/event_template_page";
import SimpleMemberDisplayName from "../forms/improved_components/member_display_name/simple_member_display_name";
import { CustomizeSettings } from "./customize_settings/customize_settings";
import { ApiSettings } from "./api_settings/api_settings";
import { ActivateButtonWithSelection } from "../forms/improved_components/premium_page/activate_button_with_selection";

interface BlurbDisplayOptions {
	is_patron_only?: boolean;
	is_big?: boolean;
	include_modify_button?: boolean;
	is_overridden?: boolean;
}

export const GuildSettingsContext = createContext<
	Pick<
		GuildSettingsProps,
		"all_text_channels" | "is_admin" | "is_premium" | "has_sesh_pro"
	> & {
		original_guild_settings: Pick<
			GuildSettings,
			| "event_channel"
			| "event_channel_override_only_admins"
			| "event_channel_require_send_messages"
		>;
	}
>(null);

export type GuildSettingsFormStateKeys = keyof GuildSettings;

export async function fetchGuildSettings(
	auth_token: DiscordAuthToken,
	guild_id: string
) {
	let request_body: GetServerSettingsRequest = {
		guild_id,
		access_token: auth_token.access_token,
		token_type: auth_token.token_type,
	};

	return fetch(`/api/get_guild_settings_data`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request_body),
	}).then((response) =>
		response.json().then((value: GuildSettingsProps) => {
			if (value.err) {
				DashboardUtils.ShowDefaultErrorAlert(value.err);
				return;
			}
			return value;
		})
	);
}

export async function fetchGuildChannelSettings(
	auth_token: DiscordAuthToken,
	guild_id: string
) {
	let request_body: GetServerSettingsRequest = {
		guild_id,
		access_token: auth_token.access_token,
		token_type: auth_token.token_type,
	};

	return fetch(`/api/get_guild_channel_settings_data`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request_body),
	}).then((response) =>
		response.json().then((value: any) => {
			if (value.err) {
				DashboardUtils.ShowDefaultErrorAlert(value.err);
				return;
			}
			return value as GuildChannelSettingsOverrides;
		})
	);
}

function render_global_calendars_text() {
	return (
		<p className="">
			<span className="has-text-weight-bold">Current Setting:</span>{" "}
			<span className="command-example--inline">/list</span> and{" "}
			<span className="command-example--inline">/link</span> commands will
			show all events that are visible to the user running the command in
			the server.
		</p>
	);
}

function render_channel_calendars_text(event_channel_set: boolean) {
	return (
		<div>
			<p className="">
				<span className="has-text-weight-bold">Current Setting:</span>{" "}
				<span className="command-example--inline">/list</span> and{" "}
				<span className="command-example--inline">/link</span> commands
				will show only the events in the channel the command was sent
				in.
			</p>

			<br />

			<p className="">
				You can override this using{" "}
				<span className="command-example--inline">/list channels:all</span> or{" "}
				<span className="command-example--inline">/link channels:all</span>. A{" "}
				<a href="/manual/#list" className="manual-link">
					channel filter
				</a>{" "}
				can also be used.
			</p>

			<br />

			<p className="">
				In all cases, only events visible to the user running the
				command will be shown.
			</p>

			{event_channel_set && (
				<p className="note mt-4">
					NOTE: There is an event channel set above. Events will still
					be sent there by default. This can be overridden using
					Channel Overrides.
				</p>
			)}
		</div>
	);
}

function render_discord_timestamps_enabled_text() {
	return (
		<p className="">
			<span className="has-text-weight-bold">Current Setting:</span> Times
			will be shown using Discord Timestamps. These will automatically
			convert to each users' local timezone. If the times are not
			displayed properly, update Discord.
		</p>
	);
}

function render_discord_timestamps_disabled_text() {
	return (
		<p className="">
			<span className="has-text-weight-bold">Current Setting:</span> Times
			will be shown using the{" "}
			<a className="manual-link" href={"#timezone_settings_blurb"}>
				Default Timezone.
			</a>
		</p>
	);
}

const LowercaseToUppercaseSectionNamesMap = {};
GuildSettingsSectionNames.forEach(
	(val) => (LowercaseToUppercaseSectionNamesMap[val.toLowerCase()] = val)
);

const ChannelOverrideSectionNames: GuildSettingsSectionType[] = [
	"General",
	"Event",
	"Poll",
];

const SectionNames: Record<GuildSettingsSectionType, string> = {
	General: "General Settings",
	Event: "Event Settings",
	Poll: "Poll Settings",
	Sync: "Sync Settings",
	Logs: "Logs",
	Premium: "Premium",
	Customize: "Customize Bot",
	API: "API Settings",
};

const SectionsToIgnorePerms: Partial<
	Record<GuildSettingsSectionType, boolean>
> = {
	Premium: true,
	Sync: true,
	Logs: true,
	Customize: true,
	API: true,
};

const SettingsSectionIcons: Record<GuildSettingsSectionType, string> = {
	General: "fas fa-cog",
	Event: "fas fa-calendar-check",
	Poll: "fas fa-poll",
	Sync: "fas fa-sync",
	Premium: "fas fa-gem",
	Logs: "fas fa-clipboard-list",
	Customize: "fas fa-palette",
	API: "fas fa-code",
} as const;

const BetaSections: Partial<Record<GuildSettingsSectionType, boolean>> = {
	Sync: true,
	API: true,
};

const AlphaSections: Partial<Record<GuildSettingsSectionType, boolean>> = {};

const NewSections: Partial<Record<GuildSettingsSectionType, boolean>> = {
	Customize: true,
	API: true,
};

export interface GuildSettingsState {
	page_to_display: JSX.Element;
	is_saving: boolean;
	save_err_txt?: string;
	loading_page: boolean;
	loading_text?: string;
	current_section: GuildSettingsSectionType;
	is_big?: boolean;
	secondary_timezone_being_removed: string[];
	revision_counter?: number;

	guild_settings: GuildSettings;

	individual_channel_settings_overrides?: GuildEntityColumns;
}

function getCurrentSectionFromURL(props: RouteComponentProps) {
	const params = new URLSearchParams(props.location.search);
	return params.get("section");
}

export class GuildSettingsForm extends React.Component<
	GuildSettingsProps & RouteComponentProps,
	GuildSettingsState
> {
	constructor(props) {
		super(props);

		document.body.style.overflowY = "scroll";

		const section_from_url = getCurrentSectionFromURL(this.props);
		let current_section: GuildSettingsSectionType =
			LowercaseToUppercaseSectionNamesMap[section_from_url];

		if (!current_section) {
			current_section = "General";

			const params = new URLSearchParams({
				section: current_section.toLowerCase(),
			});
			this.props.history.replace({
				pathname: this.props.location.pathname,
				search: params.toString(),
			});
		}

		setDocTitle("Settings", this.props.guild_data.name);

		if (this.is_channel_override_page()) {
			this.state = {
				guild_settings: cloneDeep(this.props.original_guild_settings),
				page_to_display: null,
				is_saving: false,
				loading_page: false,
				secondary_timezone_being_removed: [],
				current_section: current_section,
				revision_counter: 0,
				individual_channel_settings_overrides:
					cloneDeep(
						this.props.individual_channel_settings_overrides
					) ?? {},
			};
		} else {
			this.state = {
				guild_settings: cloneDeep(this.props.original_guild_settings),
				page_to_display: null,
				is_saving: false,
				loading_page: false,
				secondary_timezone_being_removed: [],
				current_section: current_section,
				revision_counter: 0,
			};
		}

		// const state = common_utils.createServerSettingsURLState(
		// 	this.props.auth_token.bot_client_id,
		// 	this.props.guild_data.id
		// );

		// history.replaceState(null, "", `/dashboard/#state=${state}`);

		this.saveGuildSettings = this.saveGuildSettings.bind(this);
		this.AIPage = this.AIPage.bind(this);
		this.savePreset = this.savePreset.bind(this);
		this.renderSignupOptionsPage = this.renderSignupOptionsPage.bind(this);
		this.createOneOffPage = this.createOneOffPage.bind(this);
		this.renderBlurb = this.renderBlurb.bind(this);
		this.createCurrentRSVPOptionsDisplay =
			this.createCurrentRSVPOptionsDisplay.bind(this);
		this.renderTimezonePickerPage =
			this.renderTimezonePickerPage.bind(this);
		this.renderPermissionsPage = this.renderPermissionsPage.bind(this);
		this.renderPermissionsBlurb = this.renderPermissionsBlurb.bind(this);
		this.savePrimaryTimezone = this.savePrimaryTimezone.bind(this);
		this.loadOneOffPage = this.loadOneOffPage.bind(this);
		this.createCurrentTimezoneDisplay =
			this.createCurrentTimezoneDisplay.bind(this);
		this.getPageTitle = this.getPageTitle.bind(this);
		this.saveSecondaryTimezones = this.saveSecondaryTimezones.bind(this);
		this.renderSubmitButton = this.renderSubmitButton.bind(this);
		this.SyncSettings = this.SyncSettings.bind(this);
		this.PremiumSettings = this.PremiumSettings.bind(this);
		this.deactivateServer = this.deactivateServer.bind(this);
		this.activateServer = this.activateServer.bind(this);
		this.activateSeshPro = this.activateSeshPro.bind(this);
		this.deactivateSeshPro = this.deactivateSeshPro.bind(this);
	}

	is_channel_override_page(): boolean {
		return this.props.is_channel_settings_overrides_page;
	}

	get_channel_override_settings_channel_name(): string {
		return this.props.all_text_channels.find(
			(val) => this.props.individual_channel_id == val.id
		).name;
	}

	getPageTitle(): string {
		return `${SectionNames[this.state.current_section]}`;
	}

	getSettingForChannel<Key extends keyof GuildEntityColumns>(
		key: Key
	): GuildEntityColumns[Key] {
		return getSettingForChannel(
			this.state.guild_settings,
			this.state.individual_channel_settings_overrides,
			key
		);
	}

	has_active_channel_override_for_setting_in_props(
		key: keyof GuildEntityColumns
	): boolean {
		return hasActiveChannelOverrideForSetting(
			this.props.individual_channel_settings_overrides,
			key
		);
	}

	hasActiveChannelOverrideForSetting(key: keyof GuildEntityColumns): boolean {
		return hasActiveChannelOverrideForSetting(
			this.state.individual_channel_settings_overrides,
			key
		);
	}

	guild_settings_style_get_individual_channel_setting_override_from_canonical<
		Key extends keyof GuildSettings
	>(key: Key): GuildSettings[Key] {
		return guildSettingsStyle_getIndividualChannelSettingOverride(
			this.props.original_guild_settings,
			this.props.individual_channel_settings_overrides,
			key,
			this.props
		);
	}

	guildSettingsStyle_getIndividualChannelSettingOverrideFromState<
		Key extends keyof GuildSettings
	>(key: Key): GuildSettings[Key] {
		return guildSettingsStyle_getIndividualChannelSettingOverride(
			this.state.guild_settings,
			this.state.individual_channel_settings_overrides,
			key,
			this.props
		);
	}

	hasSettingChanged(key: keyof GuildSettings) {
		let current_setting =
			this.guildSettingsStyle_getIndividualChannelSettingOverrideFromState(
				key
			);

		let original_setting =
			guildSettingsStyle_getIndividualChannelSettingOverride(
				this.props.original_guild_settings,
				this.props.individual_channel_settings_overrides,
				key,
				this.props
			);

		if (key === "prefix") {
			current_setting = current_setting ? current_setting : "!";
			original_setting = original_setting ? original_setting : "!";
		}

		if ((current_setting as any)?.id || (original_setting as any)?.id) {
			current_setting = (current_setting as any)?.id;
			original_setting = (original_setting as any)?.id;
		}

		if (key === "attendee_role_data") {
			let current_setting_as_role_data =
				current_setting as AttendeeRoleDataWithIDs;
			let original_setting_as_role_data =
				original_setting as AttendeeRoleDataWithIDs;

			return !common_utils.areAttendeeRoleDatasEqual(
				current_setting_as_role_data,
				original_setting_as_role_data
			);
		}

		const object_keys: (keyof GuildSettings)[] = [
			"default_native_event_data",
			"default_event_thread_settings",
			"event_channel_forum_tag_ids",
			"poll_channel_forum_tag_ids",
			"default_event_server_reminders",
			"default_role_mentions_on_event_create",
			"default_user_mentions_on_event_create",
		];

		if (object_keys.includes(key)) {
			return !common_utils.areObjectsEqual(
				current_setting,
				original_setting
			);
		}

		if (key === "use_reactions_for_polls") {
			return Boolean(original_setting) !== Boolean(current_setting);
		}

		// Need to explicitly handle objects, if an object makes it here, we implicitly do not handle it, so assume its unchanged
		if (
			(current_setting != null && typeof current_setting === "object") ||
			(original_setting != null && typeof original_setting === "object")
		) {
			return false;
		}

		return current_setting !== original_setting;

		// use lodash if we ever use this for non primitives.

		// return !lodash.isEqual(
		// 	this.state.guild_settings[key],
		// 	this.state.original_settings[key]
		// );
	}

	componentWillUnmount() {
		document.body.style.overflowY = "auto";
		this.props.clear_parents_settings_data_func();
	}

	updateState<Key extends GuildSettingsFormStateKeys>(
		thing: Key,
		value: GuildSettings[Key]
	) {
		let key = thing.substr(0); //wtf

		if (key in this.state.guild_settings) {
			if (this.is_channel_override_page()) {
				guildSettingsStyle_setIndividualChannelSettingOverride(
					this.state.individual_channel_settings_overrides,
					key as keyof GuildSettings,
					value,
					this.props
				);
			} else {
				this.state.guild_settings[key] = value;
			}
		} else if (key in this.state) {
			this.state[key] = value;
		} else {
			console.log("Error: Invalid Key");
			return;
		}

		this.setState(this.state);
	}

	handleSubmit(event) {
		event.preventDefault();
	}

	async savePermissions(permissions: CommandPermissions[]) {
		if (!this.props.is_admin) {
			return;
		}

		const request_body: SetGuildPermissionsRequest = {
			permissions,
			access_token: this.props.auth_token.access_token,
			token_type: this.props.auth_token.token_type,
			guild_id: this.props.guild_data.id,
			channel_id: this.props.individual_channel_id,
		};

		this.setState({
			loading_page: true,
			loading_text: "Setting Server Permissions...",
		});

		try {
			const response = await fetch(`/api/set_guild_permissions`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(request_body),
			});

			const val = await response.json();

			if (response.status === 200) {
				this.updateOriginalSettings(
					"permissions",
					val.permissions as CommandPermissions[]
				);
			} else {
				DashboardUtils.ShowDefaultErrorAlert(val.err);
			}
		} catch (err) {
			DashboardUtils.ShowDefaultErrorAlert(
				"Failed to set permissions. Please try again."
			);
		} finally {
			this.setState((prevState) => ({
				loading_page: false,
				loading_text: null,
				page_to_display: null,
				revision_counter: (prevState.revision_counter || 0) + 1,
			}));
		}
	}

	async savePreset(preset: OptionPreset) {
		const dupe = common_format_utils.get_duplicate_emojis_in_preset(preset);
		if (dupe) {
			let error = `Duplicate custom emojis [${dupe}]! Can not use the same emoji for multiple options.`;
			DashboardUtils.ShowDefaultErrorAlert(error);
			return;
		}

		const request_body: SetGuildPresetRequest = {
			preset,
			access_token: this.props.auth_token.access_token,
			token_type: this.props.auth_token.token_type,
			guild_id: this.props.guild_data.id,
			channel_id: this.props.individual_channel_id,
		};

		this.setState({
			loading_page: true,
			loading_text: "Setting Preset as Default...",
		});

		try {
			const response = await fetch(`/api/set_guild_preset`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(request_body),
			});

			if (response.status === 200) {
				this.updateOriginalSettings("signup_options", preset);
			} else {
				const val = await response.json();
				DashboardUtils.ShowDefaultErrorAlert(val.err);
			}
		} catch (err) {
			DashboardUtils.ShowDefaultErrorAlert(
				"Failed to set preset. Please try again."
			);
		} finally {
			this.setState((prevState) => ({
				loading_page: false,
				loading_text: null,
				page_to_display: null,
				revision_counter: (prevState.revision_counter || 0) + 1,
			}));
		}
	}

	updateOriginalSettings<Key extends GuildSettingsFormStateKeys>(
		key: Key,
		value: GuildSettings[Key]
	) {
		if (this.is_channel_override_page()) {
			const original_settings_clone =
				cloneDeep(this.props.individual_channel_settings_overrides) ??
				{};

			guildSettingsStyle_setIndividualChannelSettingOverride(
				original_settings_clone,
				key,
				value,
				this.props
			);

			this.props.on_save_func(
				undefined,
				original_settings_clone,
				this.props.individual_channel_id,
				this.props.guild_data.id,
				true
			);
		} else {
			const original_settings_clone = cloneDeep(
				this.props.original_guild_settings
			);
			original_settings_clone[key] = value;

			this.props.on_save_func(original_settings_clone as GuildSettings);
		}

		this.updateState(key, value);
	}

	async saveSecondaryTimezones(timezone: string, removing: boolean) {
		if (
			!removing &&
			this.guild_settings_style_get_individual_channel_setting_override_from_canonical(
				"secondary_timezones"
			).length >= Constants.SECONDARY_TIMEZONE_LIMIT
		) {
			DashboardUtils.ShowDefaultErrorAlert(
				"Secondary timezone limit reached"
			);
			return;
		}

		const request_body: SetSecondaryTimezoneRequest = {
			timezone,
			removing,
			access_token: this.props.auth_token.access_token,
			token_type: this.props.auth_token.token_type,
			guild_id: this.props.guild_data.id,
			channel_id: this.props.individual_channel_id,
		};

		if (removing) {
			const new_secondary_timezones_being_removed =
				this.state.secondary_timezone_being_removed;
			new_secondary_timezones_being_removed.push(timezone);
			this.setState({
				secondary_timezone_being_removed:
					new_secondary_timezones_being_removed,
			});
		} else {
			this.setState({
				loading_page: true,
				loading_text: "Setting Timezone...",
			});
		}

		try {
			const response = await fetch(`/api/set_guild_secondary_tz_web`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(request_body),
			});

			if (response.status === 200) {
				const original_settings =
					this.guild_settings_style_get_individual_channel_setting_override_from_canonical(
						"secondary_timezones"
					);

				if (removing) {
					this.updateOriginalSettings(
						"secondary_timezones",
						original_settings.filter((val) => val !== timezone)
					);
				} else {
					this.updateOriginalSettings(
						"secondary_timezones",
						Array.from(
							new Set(original_settings.concat([timezone]))
						)
					);
				}
			} else {
				const response_json = await response.json();
				DashboardUtils.ShowDefaultErrorAlert(response_json.err);
			}
		} catch (err) {
			DashboardUtils.ShowDefaultErrorAlert(
				"Failed to set timezone. Please try again."
			);
		} finally {
			this.setState((prevState) => ({
				loading_page: false,
				loading_text: null,
				page_to_display: null,
				is_big: false,
				secondary_timezone_being_removed:
					prevState.secondary_timezone_being_removed.filter(
						(val) => val !== timezone
					),
				revision_counter: (prevState.revision_counter || 0) + 1,
			}));
		}
	}

	async savePrimaryTimezone(timezone: string) {
		const request_body: SetGuildTimezoneRequest = {
			timezone,
			access_token: this.props.auth_token.access_token,
			token_type: this.props.auth_token.token_type,
			guild_id: this.props.guild_data.id,
			channel_id: this.props.individual_channel_id,
		};

		this.setState({
			loading_page: true,
			loading_text: "Setting Timezone...",
		});

		try {
			const response = await fetch(`/api/set_guild_tz_web`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(request_body),
			});

			if (response.status === 200) {
				this.updateOriginalSettings("timezone", timezone);
			} else {
				const val = await response.json();
				DashboardUtils.ShowDefaultErrorAlert(val.err);
			}
		} catch (err) {
			DashboardUtils.ShowDefaultErrorAlert(
				"Failed to set timezone. Please try again."
			);
		} finally {
			this.setState((prevState) => ({
				loading_page: false,
				loading_text: null,
				page_to_display: null,
				is_big: false,
				revision_counter: (prevState.revision_counter || 0) + 1,
			}));
		}
	}

	async deactivateServer() {
		const body: ModifyGuildActivationRequest = {
			guild_id: this.props.guild_data.id,
			is_activate: false,
		};

		const headers = common_utils.createSeshHeader(this.props.auth_token);

		this.setState({
			is_saving: true,
		});

		return fetch(`/api/modify_premium_status`, {
			method: "POST",
			headers,
			body: JSON.stringify(body),
		}).then((response) => {
			this.setState({
				is_saving: false,
			});

			if (response.status === 200) {
				response.json().then((val: ModifyPremiumStatusWebResponse) => {
					if (val.err) {
						DashboardUtils.ShowDefaultErrorAlert(
							common_utils.markdownToHtml(val.err)
						);
						return false;
					}
					this.props.on_premium_modified_func(
						val.guild_settings_props
					);
					return true;
				});
			} else {
				DashboardUtils.ShowDefaultErrorAlert(response.statusText);
				return false;
			}
		});
	}

	async activateServer() {
		const body: ModifyGuildActivationRequest = {
			guild_id: this.props.guild_data.id,
			is_activate: true,
		};

		const headers = common_utils.createSeshHeader(this.props.auth_token);

		this.setState({
			is_saving: true,
		});

		return fetch(`/api/modify_premium_status`, {
			method: "POST",
			headers,
			body: JSON.stringify(body),
		}).then((response) => {
			this.setState({
				is_saving: false,
			});
			if (response.status === 200) {
				return response
					.json()
					.then((val: ModifyPremiumStatusWebResponse) => {
						if (val.err) {
							DashboardUtils.ShowDefaultErrorAlert(
								common_utils.markdownToHtml(val.err)
							);
							return false;
						}

						this.props.on_premium_modified_func(
							val.guild_settings_props
						);

						DashboardUtils.ShowSuccessMessageAlert(
							"Successfully activated server!",
							`You have used ${val.result.current_activations} out of ${val.result.max_activations} of your activations.`
						);

						return true;
					});
			}
			DashboardUtils.ShowDefaultErrorAlert(response.statusText);
			return false;
		});
	}

	async activateSeshPro() {
		const body: ModifyGuildActivationRequest = {
			guild_id: this.props.guild_data.id,
			is_activate: true,
			activation_type: "sesh_pro",
		};

		const headers = common_utils.createSeshHeader(this.props.auth_token);

		this.setState({
			is_saving: true,
		});

		return fetch(`/api/modify_premium_status`, {
			method: "POST",
			headers,
			body: JSON.stringify(body),
		}).then((response) => {
			this.setState({
				is_saving: false,
			});
			if (response.status === 200) {
				return response
					.json()
					.then((val: ModifyPremiumStatusWebResponse) => {
						if (val.err) {
							DashboardUtils.ShowDefaultErrorAlert(
								common_utils.markdownToHtml(val.err)
							);
							return false;
						}

						this.props.on_premium_modified_func(
							val.guild_settings_props
						);

						DashboardUtils.ShowSuccessMessageAlert(
							"Successfully activated sesh Pro!",
							`You have used ${val.result.current_activations} out of ${val.result.max_activations} of your sesh Pro activations.`
						);

						return true;
					});
			}
			DashboardUtils.ShowDefaultErrorAlert(response.statusText);
			return false;
		});
	}

	async deactivateSeshPro() {
		const body: ModifyGuildActivationRequest = {
			guild_id: this.props.guild_data.id,
			is_activate: false,
			activation_type: "sesh_pro",
		};

		const headers = common_utils.createSeshHeader(this.props.auth_token);

		this.setState({
			is_saving: true,
		});

		return fetch(`/api/modify_premium_status`, {
			method: "POST",
			headers,
			body: JSON.stringify(body),
		}).then((response) => {
			this.setState({
				is_saving: false,
			});

			if (response.status === 200) {
				response.json().then((val: ModifyPremiumStatusWebResponse) => {
					if (val.err) {
						DashboardUtils.ShowDefaultErrorAlert(
							common_utils.markdownToHtml(val.err)
						);
						return false;
					}
					this.props.on_premium_modified_func(
						val.guild_settings_props
					);
					return true;
				});
			} else {
				DashboardUtils.ShowDefaultErrorAlert(response.statusText);
				return false;
			}
		});
	}

	saveGuildSettings() {
		if (this.is_channel_override_page()) {
			const settings_to_save = cloneDeep(
				this.state.individual_channel_settings_overrides
			);

			let request_body: ChannelOverrideSettingsSetRequest = {
				settings: settings_to_save,
				access_token: this.props.auth_token.access_token,
				token_type: this.props.auth_token.token_type,
				guild_id: this.props.guild_data.id,
				channel_id: this.props.individual_channel_id,
			};

			this.setState({ is_saving: true });

			const stringified_body = JSON.stringify(request_body, (k, v) =>
				v === undefined ? null : v
			);

			fetch(`/api/set_channel_override_settings`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: stringified_body,
			}).then((response) => {
				if (response.status === 200) {
					this.setState({
						is_saving: false,
						save_err_txt: null,
					});
					this.props.on_save_func(
						null,
						settings_to_save,
						this.props.individual_channel_id
					);
				} else {
					response.json().then((val) => {
						this.setState({
							is_saving: false,
							save_err_txt: val.err,
						});
					});
				}
			});
		} else {
			const settings_to_save = cloneDeep(this.state.guild_settings);

			let request_body: GuildSettingsSetRequest = {
				settings: settings_to_save,
				access_token: this.props.auth_token.access_token,
				token_type: this.props.auth_token.token_type,
				guild_id: this.props.guild_data.id,
			};

			this.setState({ is_saving: true });

			fetch(`/api/set_guild_settings`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(request_body),
			}).then((response) => {
				if (response.status === 200) {
					this.setState({
						is_saving: false,
						save_err_txt: null,
					});
					this.props.on_save_func(settings_to_save);
				} else {
					response.json().then((val) => {
						this.setState({
							is_saving: false,
							save_err_txt: val.err,
						});
					});
				}
			});
		}
	}

	createTimezoneListing(timezone: string) {
		let time = luxon.DateTime.utc().setZone(timezone);

		let offset_text = time.zone.formatOffset(time.toMillis(), "short");

		let result = `[UTC${offset_text}] ${timezone}`;
		if (timezone !== "UTC") {
			result += ` (${time.offsetNameShort})`;
		}

		return result;
	}

	createCurrentTimezoneDisplay() {
		const primary_timezone =
			this.guild_settings_style_get_individual_channel_setting_override_from_canonical(
				"timezone"
			);

		const primary_header = "Default Timezone";
		const secondary_header = "Additional Display Timezones";
		let button_text = "Add Display Timezone";

		let secondary_timezones =
			this.guild_settings_style_get_individual_channel_setting_override_from_canonical(
				"secondary_timezones"
			);

		let rows = [];

		const should_disable = !this.props.is_admin;

		let i = 0;
		for (let timezone of secondary_timezones) {
			let current_timezones_being_removed =
				this.state.secondary_timezone_being_removed.includes(timezone);

			rows.push(
				<tr key={i}>
					<td>{this.createTimezoneListing(timezone)}</td>
					<td>
						{!current_timezones_being_removed &&
							!should_disable && (
								<DashboardUtils.DeleteButton
									onClick={() =>
										this.saveSecondaryTimezones(
											timezone,
											true
										)
									}
								/>
							)}
						{current_timezones_being_removed && (
							<div>
								<ClipLoader
									size={"1rem"}
									loading={true}
									color={Constants.EMBED_COLOR}
								/>
							</div>
						)}
					</td>
				</tr>
			);
			i++;
		}

		if (rows.length === 0) {
			rows.push(
				<tr key="none_ele">
					<td colSpan={2}>None</td>
				</tr>
			);
		}

		const secondary_timezone_limit_reached =
			secondary_timezones.length >= Constants.SECONDARY_TIMEZONE_LIMIT;

		if (secondary_timezone_limit_reached) {
			button_text = "Timezone Limit Reached";
		}

		return (
			<div>
				<div className="columns is-mobile is-multiline !pt-1">
					<div className="column is-full">
						<table className="sesh-table mobilized-table max-w-full">
							<thead>
								<tr>
									<th className="flex items-center gap-2">
										{primary_header}{" "}
										<div className="flex w-fit items-center">
											<InfoTooltip
												info={
													<>
														The default timezone
														when creating events in
														Discord. Also used in{" "}
														<InlineCode>
															/list
														</InlineCode>{" "}
														output
													</>
												}
											/>
										</div>
									</th>
									{!should_disable && <th></th>}
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>
										{this.createTimezoneListing(
											primary_timezone
										)}
									</td>
									{!should_disable && (
										<td>
											<a
												className="manual-link"
												onClick={() => {
													this.loadOneOffPage(
														this
															.renderTimezonePickerPage,
														true,
														"timezone"
													);
												}}
											>
												<i className="fas fa-pencil-alt" />
											</a>
										</td>
									)}
								</tr>
							</tbody>
						</table>
					</div>
				</div>
				<div className="columns is-mobile">
					<div className="column is-full">
						<table className="sesh-table mobilized-table max-w-full">
							<thead>
								<tr>
									<th className="flex items-center gap-2">
										{secondary_header}{" "}
										<div className="flex w-fit items-center">
											<InfoTooltip
												info={
													"Additional timezones to display on event embeds"
												}
											/>
										</div>
									</th>
									<th></th>
								</tr>
							</thead>
							<tbody>{rows}</tbody>
						</table>
						<ModifySettingsButton
							className="mt-4"
							onClick={() =>
								this.loadOneOffPage(
									this.renderTimezonePickerPage,
									true,
									"secondary_timezones"
								)
							}
							disabled={
								secondary_timezone_limit_reached ||
								should_disable
							}
						>
							{button_text}
						</ModifySettingsButton>
					</div>
				</div>
			</div>
		);
	}

	createCurrentRSVPOptionsDisplay() {
		const signup_options =
			this.guild_settings_style_get_individual_channel_setting_override_from_canonical(
				"signup_options"
			);

		return <RSVPOptionsTable options={signup_options} />;
	}

	async loadOneOffPage(
		render_function: Function,
		is_big: boolean,
		key?: GuildSettingsFormStateKeys
	) {
		this.setState({ loading_page: true });

		let page_to_display = await render_function(key);

		if (this.state.loading_page) {
			this.setState({
				page_to_display: page_to_display,
				loading_page: false,
				is_big: is_big,
			});

			if (is_big) {
				// terrible hack to fix account info positioning
				// remove class on entrance, will be added back on exit
			} // else: loading was cancelled
		}
	}

	createModifyButton(
		render_function: Function,
		is_big: boolean,
		disabled: boolean,
		is_patron_only: boolean
	) {
		return (
			<div className="tooltip">
				<ModifySettingsButton
					className="mt-4"
					disabled={disabled}
					onClick={async () => {
						this.loadOneOffPage(render_function, is_big);
					}}
				>
					Modify
				</ModifySettingsButton>
				{is_patron_only && !this.props.is_premium && (
					<span className="tooltiptop">
						<span className="icon is-small">
							<i className="fas fa-gem"></i>
						</span>{" "}
						<span>
							<a href={Constants.PREMIUM_LINK} target="_blank">
								Premium
							</a>{" "}
							Exclusive
						</span>
					</span>
				)}
			</div>
		);
	}

	renderBlurb(
		id: string,
		header: string,
		info: string | JSX.Element,
		blurb_display: Function,
		render_function: Function,
		display_options: BlurbDisplayOptions
	) {
		let disabled =
			!this.props.is_admin ||
			(!this.props.is_premium && display_options.is_patron_only);

		let outerClassName = "field";
		if (display_options.is_overridden) {
			outerClassName += ` overridden-field`;
		} else {
			outerClassName += ` blurb`;
		}

		return (
			<div className={outerClassName} id={id}>
				{DashboardUtils.CreateHeaderAndInfoButton(header, info)}
				<div>
					{blurb_display()}{" "}
					{display_options.include_modify_button &&
						this.createModifyButton(
							render_function,
							display_options.is_big,
							disabled,
							display_options.is_patron_only
						)}
				</div>
			</div>
		);
	}

	createOneOffPage(element_func: JSX.Element) {
		return (
			<div>
				<span className="options-margin-items">
					<div
						className="columns is-mobile is-vcentered is-variable is-1 margin-link"
						onClick={() => {
							this.setState({
								page_to_display: null,
								loading_page: false,
								loading_text: null,
								is_big: false,
							});
						}}
					>
						<div className="column is-narrow">
							{DashboardUtils.GetBackIcon()}
						</div>
						<div className="column is-narrow">
							{this.state.current_section} Settings
						</div>
					</div>
				</span>
				<div className="back-arrow"></div>
				<FadeIn transitionDuration={FADE_IN_ANIMATION_DURATION}>
					{element_func}
				</FadeIn>
			</div>
		);
	}

	async renderSignupOptionsPage() {
		const current_preset =
			this.guild_settings_style_get_individual_channel_setting_override_from_canonical(
				"signup_options"
			);

		let request_body = {
			access_token: this.props.auth_token.access_token,
			token_type: this.props.auth_token.token_type,
			guild_id: this.props.guild_data.id,
		};

		const response_raw = await fetch(`/api/get_signup_option_presets`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(request_body),
		});

		const response: SignUpPresetsProp = await response_raw.json();

		if (response.err) {
			DashboardUtils.ShowDefaultErrorAlert(response.err);
			return;
		}

		return (
			<div>
				<h1 className="title is-3 has-text-centered">
					Edit Default RSVP Options
				</h1>
				<div>
					<RSVPOptionsForm
						is_premium={this.props.is_premium}
						user_presets={response.user_presets}
						user_id={response.user_id}
						custom_emojis={response.custom_emojis}
						all_guild_data={response.all_guild_data}
						current_preset={current_preset}
						updateFunc={() => {}}
						token_type={this.props.auth_token.token_type}
						access_token={this.props.auth_token.access_token}
						save_for_guild_settings_data={{
							on_click_func: (preset: OptionPreset) =>
								this.savePreset(preset),
							text: "Set as Default",
						}}
					></RSVPOptionsForm>
				</div>
			</div>
		);
	}

	renderTimezonePickerPage(field_key: GuildSettingsFormStateKeys) {
		let saveFunc: Function;
		let secondary_timezones: string[] = [];
		let is_secondary: boolean = false;
		if (field_key === "timezone") {
			saveFunc = (val) => this.savePrimaryTimezone(val);
		} else {
			saveFunc = (val) => this.saveSecondaryTimezones(val, false);
			secondary_timezones =
				this.guild_settings_style_get_individual_channel_setting_override_from_canonical(
					"secondary_timezones"
				);
			is_secondary = true;
		}

		return (
			<TimezonePickerForm
				key="timezone_picker_form"
				token_type={this.props.auth_token.token_type}
				access_token={this.props.auth_token.access_token}
				guild_id={this.props.guild_data.id}
				updateFunc={() => {}}
				current_timezone={this.guild_settings_style_get_individual_channel_setting_override_from_canonical(
					"timezone"
				)}
				secondary_timezones={secondary_timezones}
				is_secondary={is_secondary}
				saveFunc={(val) => {
					saveFunc(val);
				}}
				use_discord_timestamps={
					this.state.guild_settings.use_discord_timestamps
				}
			/>
		);
	}

	renderPermissionsPage() {
		return (
			<PermissionsForm
				initial_permissions={this.guild_settings_style_get_individual_channel_setting_override_from_canonical(
					"permissions"
				)}
				role_mentions={this.props.roles}
				save_func={(new_permissions: CommandPermissions[]) =>
					this.savePermissions(new_permissions)
				}
			/>
		);
	}

	createAdminElement() {
		return (
			<InfoTooltip
				label={"admins"}
				info={
					<>
						Users with{" "}
						<span className="has-text-weight-bold">
							Manage Sesh
						</span>{" "}
						permissions
					</>
				}
			/>
		);
	}

	renderPermissionsBlurb() {
		const rows = [];

		const admin_name = "admins*";

		const permissions =
			this.guild_settings_style_get_individual_channel_setting_override_from_canonical(
				"permissions"
			);

		let index = 0;
		for (let option of permissions) {
			let role_elements: JSX.Element[] = [];

			let has_everyone = false;

			let mentionables = option.roles_ids.map((val) => {
				if (val === "@everyone") {
					has_everyone = true;
				}
				return this.props.roles.find(
					(mentionable) => val === mentionable.id
				);
			});

			mentionables = mentionables.filter((val) => Boolean(val));

			if (has_everyone) {
				mentionables = [
					mentionables.find((val) => val.id === "@everyone"),
				];
			} else {
				mentionables.push({
					id: null,
					name: admin_name,
					type: 1,
					color: "#FFFFFF",
				});
			}

			let mention_index = 0;
			for (let role of mentionables) {
				let color = "#FFFFFF";
				let name: string | JSX.Element = role.name;
				if (role.name === admin_name) {
					name = this.createAdminElement();
				} else if (role.id !== "@everyone") {
					name = "@" + name;
				}

				let comma = mention_index < mentionables.length - 1 ? ", " : "";

				role_elements.push(
					<span key={mention_index}>
						<span style={{ color: color }}>{name}</span>
						{comma}
					</span>
				);

				mention_index++;
			}

			const name =
				option.command === "settings" ? "manager" : option.command;

			rows.push(
				<tr key={index}>
					<td>{name}</td>
					<td>{role_elements}</td>
				</tr>
			);

			index++;
		}

		return (
			<div>
				<table className="sesh-table mobilized-table">
					<thead>
						<tr>
							<th>Command</th>
							<th>Roles</th>
						</tr>
					</thead>
					<tbody>{rows}</tbody>
				</table>
			</div>
		);
	}

	renderSeshChannelSettings(
		type: "Poll" | "Event",
		initial_channel: SeshTextChannelData,
		all_channels: SeshTextChannelData[],
		is_event: boolean
	) {
		let channel_key: GuildSettingsFormStateKeys;
		let admin_override_key: GuildSettingsFormStateKeys;
		let require_send_messages_key: GuildSettingsFormStateKeys;
		let channel_forum_tags_key: GuildSettingsFormStateKeys;

		switch (type) {
			case "Event":
				channel_key = "event_channel";
				admin_override_key = "event_channel_override_only_admins";
				require_send_messages_key =
					"event_channel_require_send_messages";
				channel_forum_tags_key = "event_channel_forum_tag_ids";
				break;
			case "Poll":
				channel_key = "poll_channel";
				admin_override_key = "poll_channel_override_only_admins";
				require_send_messages_key =
					"poll_channel_require_send_messages";
				channel_forum_tags_key = "poll_channel_forum_tag_ids";
				break;
		}

		return (
			<div className="columns is-mobile is-vcentered is-multiline">
				<div className="column is-full">
					<ChannelSelectFormWithTags
						update_initially={
							!this.props.is_channel_settings_overrides_page
						}
						initial_value={initial_channel}
						channel_data={all_channels}
						disabled={!this.props.is_admin}
						updateFunc={(val) => this.updateState(channel_key, val)}
						include_threads={false}
						selected_forum_tag_ids={this.getSettingForChannel(
							channel_forum_tags_key
						)}
						setSelectedTagIds={(ids: string[]) =>
							this.updateState(channel_forum_tags_key, ids)
						}
					/>
				</div>
				<div className="column is-narrow">
					<label>
						<div className="flex items-center gap-2">
							<input
								type="checkbox"
								key={admin_override_key}
								name="has_reminders"
								checked={this.guildSettingsStyle_getIndividualChannelSettingOverrideFromState(
									admin_override_key
								)}
								onChange={(e) =>
									this.updateState(
										admin_override_key,
										e.target.checked
									)
								}
								disabled={!this.props.is_admin}
							/>{" "}
							<div>
								Only{" "}
								<span className="has-text-weight-bold">
									Admins
								</span>{" "}
								can override{" "}
							</div>
							<InfoTooltip
								info={
									<>
										Only allow users with{" "}
										<span className="has-text-weight-bold">
											Manage Sesh
										</span>{" "}
										perms to send{" "}
										{is_event ? "events" : "polls"} outside
										the designated channel
									</>
								}
							/>
						</div>
					</label>
				</div>
				<div className="column is-narrow">
					<label>
						<div className="flex items-center gap-2">
							<input
								type="checkbox"
								key={require_send_messages_key}
								name="has_reminders"
								checked={this.guildSettingsStyle_getIndividualChannelSettingOverrideFromState(
									require_send_messages_key
								)}
								onChange={(e) =>
									this.updateState(
										require_send_messages_key,
										e.target.checked
									)
								}
								disabled={!this.props.is_admin}
							/>{" "}
							<div>
								Require{" "}
								<span className="has-text-weight-bold">
									Send Messages
								</span>{" "}
								permissions{" "}
							</div>
							<InfoTooltip
								info={
									<>
										Only allow users with{" "}
										<span className="has-text-weight-bold">
											Send Messages
										</span>{" "}
										permissions in the selected channel to
										create {is_event ? "events" : "polls"}{" "}
										in the channel. If disabled, users will
										only require{" "}
										<span className="has-text-weight-bold">
											View Channel
										</span>{" "}
										permissions to create{" "}
										{is_event ? "events" : "polls"} in the
										channel.
									</>
								}
							/>
						</div>
					</label>
				</div>
			</div>
		);
	}

	SyncSettings() {
		return (
			<GuildSettingsContext.Provider value={this.props}>
				{Constants.GCAL_ENABLED ? (
					<SyncSettings />
				) : (
					<ErrorNote
						error={
							"Sync settings are currently down for maintenance. Your existing syncs are currently paused. iCal feeds will still work during this downtime. Thanks for your patience."
						}
					></ErrorNote>
				)}
			</GuildSettingsContext.Provider>
		);
	}

	PremiumSettings() {
		const [showConfetti, setShowConfetti] = useState(false);

		const guild_data = useCurrentGuildData();

		if (guild_data?.is_discord_premium) {
			return (
				<div>
					<p className="has-text-centered">
						This server's premium has been bought through Discord.
						If you'd like to modify this server's premium status,
						please follow{" "}
						<a
							className="manual-link"
							target="_blank"
							href={Constants.DISCORD_PREMIUM_MANAGEMENT_FAQ_LINK}
						>
							these steps
						</a>
						.
					</p>
				</div>
			);
		}

		return (
			<div>
				{showConfetti && <Party duration={5000} />}
				<p className="has-text-centered">
					Activate or deactivate sesh premium for this server. You
					must be subscribed to{" "}
					<a
						className="manual-link"
						href={Constants.PREMIUM_LINK}
						target="_blank"
					>
						Premium
					</a>{" "}
					in order to activate servers. Anyone in a server can
					activate a server, but only the original activator can
					deactivate it.
					<br />
					<br />
					To view and manage all of your premium servers, go{" "}
					<Link
						className="manual-link"
						to={common_utils.getPremiumPageURL()}
					>
						here
					</Link>{" "}
					or DM sesh{" "}
					<span className="command-example--inline">/premium</span>.
					<br />
					<br />
					To see what premium gets you, visit our{" "}
					<a
						href="https://sesh.fyi/premium"
						className="manual-link"
						target="_blank"
					>
						Premium page
					</a>
					.
				</p>
				<br />
				<div className="field submit-button">
					{this.props.is_premium && (
						<div className="text-semibold mb-4 text-center text-premium">
							{this.props.has_sesh_pro && this.props.sesh_pro_activator_id ? (
								<>
									✨ This server has <strong>sesh Pro</strong> activated by{" "}
									<SimpleMemberDisplayName
										include_avatar={true}
										user_id={this.props.sesh_pro_activator_id}
									/>{" "}
									✨
									<div className="text-sm mt-1">
										sesh Pro includes custom branding and API access
									</div>
								</>
							) : (
								this.props.premium_activator_id && (
									<>
										🎉 This server is already activated by{" "}
										<SimpleMemberDisplayName
											include_avatar={true}
											user_id={this.props.premium_activator_id}
										/>{" "}
										🎉
									</>
								)
							)}
						</div>
					)}
					<div className="control columns is-multiline is-mobile is-vcentered is-centered">
						<div className="column is-narrow">
							<ActivateButtonWithSelection
								is_admin={this.props.is_admin}
								is_premium={this.props.is_premium}
								has_sesh_pro={this.props.has_sesh_pro}
								premium_activator_id={
									this.props.premium_activator_id
								}
								sesh_pro_activator_id={
									this.props.sesh_pro_activator_id
								}
								user_id={this.props.user_id}
								is_saving={this.state.is_saving}
								guild_id={this.props.guild_data.id}
								guild_name={this.props.guild_data.name}
								onActivatePremium={async () => {
									const success =
										await this.activateServer();
									if (success) {
										setShowConfetti(true);
									}
									return success;
								}}
								onDeactivatePremium={() =>
									this.deactivateServer()
								}
								onActivatePro={async () => {
									const success =
										await this.activateSeshPro();
									if (success) {
										setShowConfetti(true);
									}
									return success;
								}}
								onDeactivatePro={() =>
									this.deactivateSeshPro()
								}
							/>
						</div>
					</div>
				</div>
			</div>
		);
	}

	renderPermissionSettings() {
		return (
			<>
				{!this.is_channel_override_page() &&
					this.renderBlurb(
						"permissions_blurb",
						"Permissions",
						"Modify which roles can run sesh commands",
						this.renderPermissionsBlurb,
						this.renderPermissionsPage,
						{
							is_patron_only: false,
							is_big: false,
							include_modify_button: true,
							is_overridden:
								this.has_active_channel_override_for_setting_in_props(
									"command_permissions"
								),
						}
					)}
			</>
		);
	}

	renderGeneralSettings() {
		return (
			<>
				{this.renderBlurb(
					"timezone_settings_blurb",
					"Timezone Settings",
					null,
					this.createCurrentTimezoneDisplay,
					this.renderTimezonePickerPage,
					{
						is_patron_only: false,
						is_big: true,
						include_modify_button: false,
						is_overridden:
							this.has_active_channel_override_for_setting_in_props(
								"custom_timezone"
							) ||
							this.has_active_channel_override_for_setting_in_props(
								"secondary_timezones"
							),
					}
				)}
				{!this.is_channel_override_page() &&
					this.renderBlurb(
						"permissions_blurb",
						"Permissions",
						"Modify which roles can run sesh commands",
						this.renderPermissionsBlurb,
						this.renderPermissionsPage,
						{
							is_patron_only: false,
							is_big: false,
							include_modify_button: true,
							is_overridden:
								this.has_active_channel_override_for_setting_in_props(
									"command_permissions"
								),
						}
					)}
				<SeshAccordian
					initial_value={this.getSettingForChannel("use_24h_clock")}
					opened_initially={Boolean(
						this.getSettingForChannel("use_24h_clock")
					)}
					label="Use 24 Hour Clock"
					field_key="use_24h_clock"
					is_server_premium={this.props.is_premium}
					info={
						"Use 24 hour clock instead of 12 hour clock. Ex: 23:00 instead 11:00pm"
					}
					render_child={null}
					updateFunc={(key, value) => this.updateState(key, value)}
					disabled={!this.props.is_admin}
					unsaved_changes={this.hasSettingChanged("use_24h_clock")}
					is_overridden={this.hasActiveChannelOverrideForSetting(
						"use_24h_clock"
					)}
				/>
				<SeshAccordian
					initial_value={this.getSettingForChannel(
						"auto_remove_user_commands"
					)}
					opened_initially={Boolean(
						this.getSettingForChannel("auto_remove_user_commands")
					)}
					label="Clean User Commands"
					field_key="clean_user_commands"
					is_server_premium={this.props.is_premium}
					info={
						"Delete user command messages after they are processed"
					}
					render_child={null}
					updateFunc={(key, value) => this.updateState(key, value)}
					disabled={!this.props.is_admin}
					unsaved_changes={this.hasSettingChanged(
						"clean_user_commands"
					)}
					is_overridden={this.hasActiveChannelOverrideForSetting(
						"auto_remove_user_commands"
					)}
				/>
				{!this.is_channel_override_page() && (
					<SeshAccordian
						initial_value={
							this.state.guild_settings
								.display_attendees_as_plain_text
						}
						opened_initially={Boolean(
							this.state.guild_settings
								.display_attendees_as_plain_text
						)}
						label="Display Usernames as Plain Text"
						field_key="display_attendees_as_plain_text"
						is_server_premium={this.props.is_premium}
						info={
							<>
								Display attendees without mention formatting.
								Avoids Discord issue of displaying names as{" "}
								<span className="command-example--inline">
									{"<@12345678910>"}
								</span>{" "}
								instead of{" "}
								<span className="command-example--inline">
									@username
								</span>
							</>
						}
						render_child={null}
						updateFunc={(key, value) =>
							this.updateState(key, value)
						}
						disabled={!this.props.is_admin}
						unsaved_changes={this.hasSettingChanged(
							"display_attendees_as_plain_text"
						)}
					/>
				)}
				{!this.is_channel_override_page() && (
					<SeshAccordian
						initial_value={
							!Boolean(
								this.state.guild_settings.use_discord_timestamps
							)
						}
						opened_initially={
							!Boolean(
								this.state.guild_settings.use_discord_timestamps
							)
						}
						label="Use Legacy Time Display"
						field_key="use_discord_timestamps"
						is_server_premium={this.props.is_premium}
						info={
							"By default sesh uses Discord Timestamps. Enable this setting to use the old server-timezone-based display."
						}
						render_child={render_discord_timestamps_disabled_text}
						disabled_element_func={
							render_discord_timestamps_enabled_text
						}
						updateFunc={(key, value) => {
							this.updateState(key, !value);
						}}
						disabled={!this.props.is_admin}
						unsaved_changes={this.hasSettingChanged(
							"use_discord_timestamps"
						)}
					/>
				)}
				{/* {!this.is_channel_override_page() && (
					<div className="field flex items-center gap-2">
						<div className="text-base font-bold sm:text-xl">
							sesh AI
						</div>
						<Tag className="font-bold" type="Dark">
							New
						</Tag>
						<div className="ml-auto">
							<ModifySettingsButton
								onClick={() =>
									this.setState({
										page_to_display: <this.AIPage />,
									})
								}
							>
								View
							</ModifySettingsButton>
						</div>
					</div>
				)} */}
			</>
		);
	}

	renderPollSettings() {
		return (
			<>
				<SeshAccordian
					field_key="poll_channel"
					initial_value={this.guildSettingsStyle_getIndividualChannelSettingOverrideFromState(
						"poll_channel"
					)}
					opened_initially={Boolean(
						this.guildSettingsStyle_getIndividualChannelSettingOverrideFromState(
							"poll_channel"
						)
					)}
					label="Poll Channel"
					is_server_premium={this.props.is_premium}
					info="Channel where polls will get sent to by default"
					disabled={!this.props.is_admin}
					onCheckboxUnclicked={() => {
						this.updateState("poll_channel", undefined);
						this.updateState("poll_channel_forum_tag_ids", []);
					}}
					unsaved_changes={
						this.hasSettingChanged("poll_channel") ||
						this.hasSettingChanged(
							"poll_channel_override_only_admins"
						) ||
						this.hasSettingChanged(
							"poll_channel_require_send_messages"
						) ||
						this.hasSettingChanged("poll_channel_forum_tag_ids")
					}
					render_child={(field_key, initial_value) => {
						return this.renderSeshChannelSettings(
							"Poll",
							initial_value,
							this.props.all_text_channels,
							false
						);
					}}
					is_overridden={
						this.hasActiveChannelOverrideForSetting(
							"polls_channel_id"
						) ||
						this.hasActiveChannelOverrideForSetting(
							"poll_channel_override_only_admins"
						) ||
						this.hasActiveChannelOverrideForSetting(
							"poll_channel_require_send_messages"
						) ||
						this.hasActiveChannelOverrideForSetting(
							"poll_channel_forum_tag_ids"
						)
					}
				/>

				<SeshAccordian
					initial_value={this.getSettingForChannel(
						"can_add_poll_options"
					)}
					opened_initially={Boolean(
						this.getSettingForChannel("can_add_poll_options")
					)}
					label="Allow Anyone To Add Poll Options"
					field_key="poll_add"
					is_server_premium={this.props.is_premium}
					info={
						"Allow any user to add poll options on sesh polls by default"
					}
					render_child={null}
					updateFunc={(key, value) => this.updateState(key, value)}
					disabled={!this.props.is_admin}
					unsaved_changes={this.hasSettingChanged("poll_add")}
					is_overridden={this.hasActiveChannelOverrideForSetting(
						"can_add_poll_options"
					)}
				/>

				<SeshAccordian
					initial_value={this.getSettingForChannel(
						"is_default_poll_type_single_vote"
					)}
					opened_initially={Boolean(
						this.getSettingForChannel(
							"is_default_poll_type_single_vote"
						)
					)}
					label="Default Polls to Single Vote Per User"
					field_key="is_default_poll_type_single_vote"
					is_server_premium={this.props.is_premium}
					info={
						"By default users can vote for multiple options in polls. Enable this setting to limit users to one option per poll by default. This can be overriden using the poll creation page or the 'single_vote' parameter in /poll."
					}
					render_child={null}
					updateFunc={(key, value) => this.updateState(key, value)}
					disabled={!this.props.is_admin}
					unsaved_changes={this.hasSettingChanged(
						"is_default_poll_type_single_vote"
					)}
					is_overridden={this.hasActiveChannelOverrideForSetting(
						"is_default_poll_type_single_vote"
					)}
				/>
				<ColorPickerAccordian
					field_key={"default_poll_embed_color"}
					label={"Poll Color"}
					is_premium={this.props.is_premium}
					disabled={!this.props.is_admin}
					is_new
					color={this.getSettingForChannel(
						"default_poll_embed_color"
					)}
					unsaved_changes={this.hasSettingChanged(
						"default_poll_embed_color"
					)}
					info={"The default color for poll embeds"}
					updateFunc={(value: string) =>
						this.updateState("default_poll_embed_color", value)
					}
					is_channel_override_active={this.hasActiveChannelOverrideForSetting(
						"default_poll_embed_color"
					)}
				/>
				{!this.is_channel_override_page() && (
					<SeshAccordian
						initial_value={Boolean(
							this.state.guild_settings.use_reactions_for_polls
						)}
						opened_initially={Boolean(
							this.state.guild_settings.use_reactions_for_polls
						)}
						label="Use Reactions for Polls"
						field_key="use_reactions_for_polls"
						is_server_premium={this.props.is_premium}
						info={"Use reactions for polls instead of buttons."}
						render_child={null}
						updateFunc={(key, value) => {
							this.updateState(key, value);
						}}
						disabled={!this.props.is_admin}
						unsaved_changes={this.hasSettingChanged(
							"use_reactions_for_polls"
						)}
					/>
				)}
			</>
		);
	}

	renderEventSettings() {
		return (
			<>
				{this.renderBlurb(
					"signup_options_blurb",
					"Default RSVP Options",
					"Set the default reactions on events made in Discord",
					this.createCurrentRSVPOptionsDisplay,
					this.renderSignupOptionsPage,
					{
						is_patron_only: false,
						is_big: false,
						include_modify_button: true,
						is_overridden:
							this.has_active_channel_override_for_setting_in_props(
								"default_signup_options"
							),
					}
				)}
				<ServerRemindersForm
					is_disabled={!this.props.is_admin}
					reminders={this.guildSettingsStyle_getIndividualChannelSettingOverrideFromState(
						"default_event_server_reminders"
					)}
					setReminders={(val) =>
						this.updateState("default_event_server_reminders", val)
					}
					has_unsaved_changes={
						this.hasSettingChanged(
							"default_event_server_reminders"
						) ||
						this.hasSettingChanged(
							"default_user_mentions_on_event_create"
						) ||
						this.hasSettingChanged(
							"default_role_mentions_on_event_create"
						)
					}
					is_channel_override_active={
						this.hasActiveChannelOverrideForSetting(
							"default_role_mentions_on_event_create"
						) ||
						this.hasActiveChannelOverrideForSetting(
							"default_user_mentions_on_event_create"
						) ||
						this.hasActiveChannelOverrideForSetting(
							"default_event_server_reminders"
						)
					}
					initial_mentions={{
						create_role_mentions:
							this.guildSettingsStyle_getIndividualChannelSettingOverrideFromState(
								"default_role_mentions_on_event_create"
							),
						create_user_mentions:
							this.guildSettingsStyle_getIndividualChannelSettingOverrideFromState(
								"default_user_mentions_on_event_create"
							),
					}}
					setInitialMentions={(val) => {
						this.updateState(
							"default_role_mentions_on_event_create",
							val.create_role_mentions
						);
						this.updateState(
							"default_user_mentions_on_event_create",
							val.create_user_mentions
						);
					}}
				/>
				<SeshAccordian
					field_key="event_channel"
					initial_value={this.guildSettingsStyle_getIndividualChannelSettingOverrideFromState(
						"event_channel"
					)}
					opened_initially={Boolean(
						this.guildSettingsStyle_getIndividualChannelSettingOverrideFromState(
							"event_channel"
						)
					)}
					onCheckboxUnclicked={() => {
						this.updateState("event_channel", undefined);
						this.updateState("event_channel_forum_tag_ids", []);
					}}
					label="Event Channel"
					is_server_premium={this.props.is_premium}
					info="Channel where events will get sent to by default"
					disabled={!this.props.is_admin}
					unsaved_changes={
						this.hasSettingChanged("event_channel") ||
						this.hasSettingChanged(
							"event_channel_override_only_admins"
						) ||
						this.hasSettingChanged(
							"event_channel_require_send_messages"
						) ||
						this.hasSettingChanged("event_channel_forum_tag_ids")
					}
					render_child={(field_key, initial_value) => {
						return this.renderSeshChannelSettings(
							"Event",
							initial_value,
							this.props.all_text_channels,
							true
						);
					}}
					is_overridden={
						this.hasActiveChannelOverrideForSetting(
							"events_channel_id"
						) ||
						this.hasActiveChannelOverrideForSetting(
							"event_channel_override_only_admins"
						) ||
						this.hasActiveChannelOverrideForSetting(
							"event_channel_require_send_messages"
						) ||
						this.hasActiveChannelOverrideForSetting(
							"event_channel_forum_tag_ids"
						)
					}
				/>
				<DiscordNativeEventForm
					is_guild_setting={true}
					is_loading={false}
					voice_channels={this.props.all_voice_channels}
					initial_event_data={this.guildSettingsStyle_getIndividualChannelSettingOverrideFromState(
						"default_native_event_data"
					)}
					updateFunc={(val) =>
						this.updateState("default_native_event_data", val)
					}
					sesh_has_manage_events={this.props.sesh_has_manage_events}
					user_has_manage_events={this.props.user_has_manage_events}
					label="Mirror Events to Discord Native Events"
					is_overridden={
						this.hasActiveChannelOverrideForSetting(
							"default_native_event_location_type"
						) ||
						this.hasActiveChannelOverrideForSetting(
							"default_native_event_location_value"
						) ||
						this.hasActiveChannelOverrideForSetting(
							"default_native_event_custom_description"
						) ||
						this.hasActiveChannelOverrideForSetting(
							"default_native_event_image_url"
						)
					}
					unsaved_changes={this.hasSettingChanged(
						"default_native_event_data"
					)}
					is_disabled={!this.props.is_admin}
					rsvp_options={undefined}
				/>

				<EventThreadAccordian
					type="GuildSetting"
					is_unsaved={this.hasSettingChanged(
						"default_event_thread_settings"
					)}
					value={this.guildSettingsStyle_getIndividualChannelSettingOverrideFromState(
						"default_event_thread_settings"
					)}
					is_channel_override_active={this.hasActiveChannelOverrideForSetting(
						"default_event_thread_settings"
					)}
					onUpdate={(val) =>
						this.updateState("default_event_thread_settings", val)
					}
					disabled={!this.props.is_admin}
				/>
				<SeshAccordian
					key="attendee_role_data to be removed"
					field_key="attendee_role_data"
					is_server_premium={this.props.is_premium}
					disabled={!this.props.is_admin}
					label="Event Attendee Roles"
					initial_value={this.guildSettingsStyle_getIndividualChannelSettingOverrideFromState(
						"attendee_role_data"
					)}
					opened_initially={
						this.guildSettingsStyle_getIndividualChannelSettingOverrideFromState(
							"attendee_role_data"
						) &&
						this.guildSettingsStyle_getIndividualChannelSettingOverrideFromState(
							"attendee_role_data"
						)?.role_ids?.length > 0
					}
					onCheckboxUnclicked={() =>
						this.updateState("attendee_role_data", undefined)
					}
					info="Discord roles users will be granted upon RSVP or Event Start for all events"
					unsaved_changes={this.hasSettingChanged(
						"attendee_role_data"
					)}
					render_child={(field_key, initial_value) => {
						return (
							<AddRoleToAttendeesComponent
								value={
									initial_value ?? {
										role_ids: [],
										remove_on_end:
											Constants.DEFAULT_REMOVE_ROLE_ON_EVENT_END,
										role_add_time:
											Constants.DEFAULT_ADD_ROLE_ON_RSVP_ADD_TIME,
									}
								}
								setValue={(val) =>
									this.updateState("attendee_role_data", val)
								}
								sesh_has_manage_roles={
									this.props.sesh_has_manage_roles
								}
								sesh_role_name={this.props.sesh_role_name}
								disabled={!this.props.is_admin}
							></AddRoleToAttendeesComponent>
						);
					}}
					is_overridden={
						this.hasActiveChannelOverrideForSetting(
							"default_attendee_role_ids"
						) ||
						this.hasActiveChannelOverrideForSetting(
							"role_on_rsvp_add_time"
						) ||
						this.hasActiveChannelOverrideForSetting(
							"remove_roles_on_event_end"
						)
					}
				></SeshAccordian>
				<SeshAccordian
					initial_value={this.getSettingForChannel(
						"auto_remove_event_messages"
					)}
					opened_initially={Boolean(
						this.getSettingForChannel("auto_remove_event_messages")
					)}
					label="Clean Event Messages"
					field_key="clean_event_messages"
					is_server_premium={this.props.is_premium}
					info={
						"Delete event embeds and event start messages when an event ends"
					}
					render_child={null}
					updateFunc={(key, value) => this.updateState(key, value)}
					disabled={!this.props.is_admin}
					unsaved_changes={this.hasSettingChanged(
						"clean_event_messages"
					)}
					is_overridden={this.hasActiveChannelOverrideForSetting(
						"auto_remove_event_messages"
					)}
				/>
				<SeshAccordian
					initial_value={this.getSettingForChannel(
						"default_hide_attendees"
					)}
					opened_initially={Boolean(
						this.getSettingForChannel("default_hide_attendees")
					)}
					label="Hide Attendee Names on Embed"
					field_key="default_hide_attendees"
					is_server_premium={this.props.is_premium}
					info={
						"Hide event attendees by default for new events. This can be overridden on the Create Event page or by editing an existing event."
					}
					render_child={null}
					updateFunc={(key, value) => this.updateState(key, value)}
					disabled={!this.props.is_admin}
					unsaved_changes={this.hasSettingChanged(
						"default_hide_attendees"
					)}
					is_overridden={this.hasActiveChannelOverrideForSetting(
						"default_hide_attendees"
					)}
				/>
				<ColorPickerAccordian
					field_key={"default_event_embed_color"}
					label={"Event Color"}
					is_premium={this.props.is_premium}
					disabled={!this.props.is_admin}
					is_new
					color={this.getSettingForChannel(
						"default_event_embed_color"
					)}
					unsaved_changes={this.hasSettingChanged(
						"default_event_embed_color"
					)}
					info={"The default color for event embeds"}
					updateFunc={(value: string) =>
						this.updateState("default_event_embed_color", value)
					}
					is_channel_override_active={this.hasActiveChannelOverrideForSetting(
						"default_event_embed_color"
					)}
				/>
				{!this.is_channel_override_page() && (
					<SeshAccordian
						initial_value={Boolean(
							this.state.guild_settings.use_channel_calendars_mode
						)}
						opened_initially={Boolean(
							this.state.guild_settings.use_channel_calendars_mode
						)}
						label="Use Separate Channel Calendars"
						field_key="use_channel_calendars_mode"
						is_server_premium={this.props.is_premium}
						info={
							<>
								This setting determines what is shown by{" "}
								<InlineCode>/list</InlineCode> and{" "}
								<InlineCode>/link</InlineCode>. If you want to
								limit event visibilty to those inside of a
								channel, you should enable this.
							</>
						}
						render_child={() =>
							render_channel_calendars_text(
								Boolean(this.state.guild_settings.event_channel)
							)
						}
						disabled_element_func={render_global_calendars_text}
						updateFunc={(key, value) => {
							this.updateState(key, value);
						}}
						disabled={!this.props.is_admin}
						unsaved_changes={this.hasSettingChanged(
							"use_channel_calendars_mode"
						)}
					/>
				)}
				{!this.is_channel_override_page() && (
					<ResetCalendarFeedsBlurb
						is_admin={this.props.is_admin}
						discord_auth_token={this.props.auth_token}
						guild_id={this.props.guild_data.id}
					/>
				)}
				{!this.is_channel_override_page() && (
					<EventTemplateBlurb
						onClick={() =>
							this.setState({
								page_to_display: (
									<div className="flex flex-col items-center gap-4 ">
										<h1 className="text-[2rem] font-bold">
											<EventTemplatesHeader />
										</h1>
										<EventTemplateContents
											is_from_guild_settings
											is_guild_premium={
												this.props.is_premium
											}
										/>
									</div>
								),
							})
						}
					/>
				)}
			</>
		);
	}

	renderSubmitButton() {
		return (
			<div className="field submit-button">
				<div className="control columns is-multiline is-mobile is-vcentered">
					<div className="column is-narrow">
						<button
							className="button dashboard-primary-button is-size-4-tablet is-size-5-mobile"
							type="submit"
							onClick={this.saveGuildSettings}
							disabled={
								this.state.is_saving || !this.props.is_admin
							}
						>
							{this.state.is_saving && (
								<div className="beat-loader-offset">
									<BeatLoader
										margin={"0.5em"}
										loading={true}
										color={"#FFFFFF"}
									/>
								</div>
							)}
							{!this.state.is_saving && (
								<span className="has-text-weight-bold">
									Save Settings
								</span>
							)}
						</button>
					</div>

					{this.state.is_saving && (
						<div className="column">Saving...</div>
					)}
					{!this.state.is_saving && this.state.save_err_txt && (
						<div className="column error-text">
							{this.state.save_err_txt}
						</div>
					)}
				</div>
			</div>
		);
	}

	AIPage(props: {}) {
		const ai_toggle_description = (
			<div className="flex flex-col">
				<div>
					Grants server-wide access to the GPT-powered{" "}
					<span className="command-example">/ai</span> command.
				</div>
				<div className="ml-auto mt-4 text-xs">
					By enabling this command you agree to sesh's{" "}
					<a className="manual-link" href="/policies">
						Terms of Service
					</a>
				</div>
			</div>
		);

		return (
			<GuildSettingsContext.Provider value={this.props}>
				<AISettingsPage
					onPremiumClicked={() =>
						this.setState({
							current_section: "Premium",
						})
					}
					ai_toggler={
						<div className="server-settings-form">
							<SeshAccordian
								initial_value={Boolean(
									this.state.guild_settings.is_ai_enabled
								)}
								opened_initially={Boolean(
									this.state.guild_settings.is_ai_enabled
								)}
								label="Enable AI Command"
								field_key={"is_ai_enabled"}
								is_server_premium={this.props.is_premium}
								info={
									<div>
										Grants server-wide access to the
										GPT-powered{" "}
										<span className="command-example">
											/ai
										</span>{" "}
										command.
									</div>
								}
								render_child={() => ai_toggle_description}
								disabled_element_func={() =>
									ai_toggle_description
								}
								updateFunc={(key, value) => {
									this.updateState(key, value);
								}}
								disabled={!this.props.is_admin}
								unsaved_changes={this.hasSettingChanged(
									"is_ai_enabled"
								)}
							/>
							{this.renderSubmitButton()}
						</div>
					}
				/>
			</GuildSettingsContext.Provider>
		);
	}

	renderCurrentSection() {
		switch (this.state.current_section) {
			case "General":
				return (
					<>
						{this.renderGeneralSettings()}
						{this.renderSubmitButton()}
					</>
				);
			case "Event":
				return (
					<>
						{this.renderEventSettings()}
						{this.renderSubmitButton()}
					</>
				);
			case "Poll":
				return (
					<>
						{this.renderPollSettings()}
						{this.renderSubmitButton()}
					</>
				);
			case "Sync":
				return (
					<>
						<this.SyncSettings />
					</>
				);
			case "Logs":
				return (
					<GuildLogsPage
						is_premium={this.props.is_premium}
						renderOneOffPage={(element: JSX.Element) =>
							this.setState({
								page_to_display: element,
							})
						}
					/>
				);
			case "Premium":
				return (
					<>
						<this.PremiumSettings />
					</>
				);
			case "Customize":
				return (
					<>
						<CustomizeSettings is_admin={this.props.is_admin} />
					</>
				);
			case "API":
				return (
					<>
						<ApiSettings is_admin={this.props.is_admin} />
					</>
				);
		}
	}

	render_overview() {
		const should_display_manage_server_text =
			!this.props.is_admin &&
			!SectionsToIgnorePerms[this.state.current_section];

		const channel_settings_button = (
			<div className="column">
				<div className="is-pulled-right">
					<button
						className="button dashboard-primary-button--tiny"
						type="submit"
						onClick={() =>
							this.props.history.push(
								common_utils.getGuildChannelPickerURL(
									this.props.guild_data.id
								)
							)
						}
						disabled={
							!this.props.is_premium || !this.props.is_admin
						}
					>
						{(!this.props.is_premium && (
							<a
								href={Constants.PREMIUM_LINK}
								target="_blank"
								className="white-link-text"
							>
								<span>
									<i className="fas fa-lock button-icon"></i>{" "}
									Channel Overrides
								</span>
							</a>
						)) || (
							<span className="has-text-weight-bold">
								{!this.props.is_admin && (
									<span>
										<i className="fas fa-lock button-icon"></i>{" "}
									</span>
								)}
								Channel Overrides
							</span>
						)}
					</button>
				</div>
			</div>
		);

		const link_chain_data = this.is_channel_override_page()
			? [
					DashboardUtils.GetGuildSelectionLinkChainData(),
					DashboardUtils.RenderGuildOptionSelectLinkChain(
						this.props.guild_data
					),
					DashboardUtils.RenderGuildSettingsLinkChain(
						this.props.guild_data.id
					),
					DashboardUtils.RenderGuildChannelPickerLinkChain(
						this.props.guild_data.id
					),
					DashboardUtils.RenderGuildChannelSettingsLinkChain(
						this.props.guild_data.id,
						this.props.individual_channel_id,
						this.get_channel_override_settings_channel_name()
					),
			  ]
			: [
					DashboardUtils.GetGuildSelectionLinkChainData(),
					DashboardUtils.RenderGuildOptionSelectLinkChain(
						this.props.guild_data
					),
					DashboardUtils.RenderGuildSettingsLinkChain(
						this.props.guild_data.id
					),
			  ];

		const should_show_channel_overrides_button =
			!this.is_channel_override_page() &&
			ChannelOverrideSectionNames.includes(this.state.current_section);

		return (
			<div
				key={this.state.current_section}
				className="flex h-full flex-col"
			>
				<div className="columns is-mobile is-multiline !mb-0">
					<div className="column is-narrow settings-link-chain-container">
						<DashboardUtils.RenderLinkChain
							link_chain_data={link_chain_data}
						/>
					</div>
					{should_show_channel_overrides_button &&
						channel_settings_button}
				</div>
				<h1 className="title is-3 has-text-centered mt-6">
					<div>
						<i
							className={`fa-lg title-icon ${
								SettingsSectionIcons[this.state.current_section]
							}`}
						></i>
						{this.getPageTitle()}
						{BetaSections[this.state.current_section] && (
							<div className="tag beta-tag ml-2 !text-base">
								<span className="!text-sesh-primary">Beta</span>
							</div>
						)}
						{AlphaSections[this.state.current_section] && (
							<div className="tag alpha-tag ml-2">
								<span className="">Alpha</span>
							</div>
						)}
						{this.is_channel_override_page() && (
							<div className="is-size-6 overridden-text">
								Overriding server settings for{" "}
								<span className="has-text-weight-bold">
									#
									{this.get_channel_override_settings_channel_name()}
								</span>
							</div>
						)}
					</div>
				</h1>
				<FadeIn
					key={this.state.current_section}
					transitionDuration={FADE_IN_ANIMATION_DURATION}
					childClassName="h-full"
					className="h-full"
				>
					<form
						className="server-settings-form h-full"
						onSubmit={this.handleSubmit}
					>
						{should_display_manage_server_text && (
							<span>
								<h2 className="error-note error-text">
									Must have{" "}
									<span className="has-text-weight-bold">
										Manage Sesh
									</span>{" "}
									permissions to change sesh settings
								</h2>
								<br />
							</span>
						)}

						{this.renderCurrentSection()}
					</form>
				</FadeIn>
			</div>
		);
	}

	areUnsavedChangesPresent() {
		for (const key in this.state.guild_settings) {
			if (this.hasSettingChanged(key as keyof GuildSettings)) {
				return true;
			}
		}
		return false;
	}

	componentDidUpdate(
		prevProps: GuildSettingsProps & RouteComponentProps,
		prevState: GuildSettingsState
	) {
		if (prevState.current_section !== this.state.current_section) {
			// Reset settings to original settings on section switch
			this.setState({
				guild_settings: cloneDeep(this.props.original_guild_settings),
				individual_channel_settings_overrides:
					cloneDeep(
						this.props.individual_channel_settings_overrides
					) ?? {},
			});

			const params = new URLSearchParams({
				section: this.state.current_section.toLowerCase(),
			});
			this.props.history.replace({
				pathname: this.props.location.pathname,
				search: params.toString(),
			});
		}
	}

	render() {
		let current_render: JSX.Element;
		let key: string;
		if (this.state.loading_page) {
			current_render = this.createOneOffPage(
				DashboardUtils.RenderLoadingThrobber(this.state.loading_text)
			);
			key = "loading";
		} else {
			current_render = this.state.page_to_display
				? this.createOneOffPage(this.state.page_to_display)
				: this.render_overview();

			key = this.state.page_to_display 
				? `one_off_${this.state.revision_counter || 0}` 
				: `main_page_${this.state.revision_counter || 0}`;
		}

		const containerClassName = this.state.is_big
			? "column is-8 create-event-container"
			: "column is-full-mobile is-9-tablet is-8-desktop is-7-widescreen is-5-fullhd create-event-container";

		//is-three-quarters-desktop is-half-widescreen is-two-fifths-fullhd

		// is-full-desktop
		// is-10-widescreen
		// is-9-full-hd

		const sidebar_disabled =
			this.state.is_saving || this.state.loading_page;

		return (
			<FadeIn transitionDuration={FADE_IN_ANIMATION_DURATION}>
				<div
					className="columns is-mobile"
					style={{ transition: "opacity 0.5s", opacity: "1" }}
				>
					<div className="column is-full">
						<MenuSidebar
							current_section={this.state.current_section}
							setSection={(section) => {
								if (
									!this.state.page_to_display &&
									section === this.state.current_section
								) {
									// Ignore section click if we're not on a one off page or if clicked current section
									return;
								}

								if (this.areUnsavedChangesPresent()) {
									DashboardUtils.ShowDefaultWarningAlert(
										"You have unsaved changes on this page. Proceeding will discard any unsaved changes you have made.",
										"Unsaved Changes"
									).then((val) => {
										if (val.isConfirmed) {
											this.setState({
												current_section: section,
												page_to_display: null,
												loading_page: false,
												loading_text: null,
												is_big: false,
											});
										}
									});
								} else {
									this.setState({
										current_section: section,
										page_to_display: null,
										loading_page: false,
										loading_text: null,
										is_big: false,
									});
								}
							}}
							guild_id={this.props.guild_data.id}
							is_admin={this.props.is_admin}
							is_premium={this.props.is_premium}
							is_channel_override={this.is_channel_override_page()}
							is_disabled={sidebar_disabled}
						>
							<div key={key} className={containerClassName}>
								{current_render}
							</div>
						</MenuSidebar>
					</div>
				</div>
				<UnsavedChangesBanner
					isVisible={this.areUnsavedChangesPresent()}
					onSave={() => this.saveGuildSettings()}
					onRevert={() => {
						this.setState({
							guild_settings: cloneDeep(this.props.original_guild_settings),
							individual_channel_settings_overrides:
								cloneDeep(
									this.props.individual_channel_settings_overrides
								) ?? {},
							revision_counter: (this.state.revision_counter || 0) + 1,
						});
					}}
					isSaving={this.state.is_saving}
				/>
			</FadeIn>
		);
	}
}

function SidebarOption(props: {
	onClick: (section: GuildSettingsSectionType) => void;
	section: GuildSettingsSectionType;
	is_active: boolean;
	is_disabled: boolean;
}) {
	const classNames: string[] = [
		"column is-full-tablet is-narrow-mobile menu-item has-text-centered",
	];

	if (props.is_active) {
		classNames.push("is-active");
	} else if (props.is_disabled) {
		classNames.push("input-disabled--severe");
	}

	const icon_class_names: string[] = [
		`${SettingsSectionIcons[props.section]} section-icon`,
	];

	if (props.section === "Premium") {
		// make this icon smaller because its weirdly bigger
		icon_class_names.push("smaller-icon");
	}

	return (
		<div
			className={classNames.join(" ")}
			onClick={() =>
				props.is_disabled ? undefined : props.onClick(props.section)
			}
		>
			<span className="icon is-medium is-hidden-tablet">
				<i className={`${icon_class_names.join(" ")} fa-sm`}></i>
			</span>
			<span className="icon is-large is-hidden-mobile">
				<i className={`${icon_class_names.join(" ")} fa-lg`}></i>
			</span>
			<div className="is-hidden-tablet-only is-hidden-mobile">
				<div className="flex flex-col items-center">
					<div className="flex gap-2">
						<div className="">{props.section}</div>
						{NewSections[props.section] && (
							<Tag type="Dark" className="font-bold">
								New
							</Tag>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

function MenuSidebar(props: {
	current_section: GuildSettingsSectionType;
	setSection: (section: GuildSettingsSectionType) => void;
	guild_id: string;
	is_premium: boolean;
	is_admin: boolean;
	is_channel_override: boolean;
	is_disabled: boolean;
	children: JSX.Element;
}) {
	const is_mobile = useIsMobile();

	const menu_sections: JSX.Element[] = [];
	for (const section_name of GuildSettingsSectionNames) {
		if (
			props.is_channel_override &&
			!ChannelOverrideSectionNames.includes(section_name)
		) {
			continue;
		}

		menu_sections.push(
			<SidebarOption
				key={section_name}
				section={section_name}
				is_active={props.current_section === section_name}
				onClick={props.setSection}
				is_disabled={props.is_disabled}
			/>
		);
	}

	if (is_mobile) {
		return (
			<div>
				<div className="columns is-mobile is-centered is-vcentered !m-0 !p-0">
					<div className="column is-full !m-0 !p-0">
						<div className="is-pulled-right">
							<div className="columns is-mobile is-centered is-vcentered !m-0 !p-0">
								<div className="column is-narrow mobile-sidebar-container sesh-sidebar">
									<div className="columns is-mobile menu-sections is-vcentered">
										{menu_sections}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				{props.children}
			</div>
		);
	}

	return (
		<div className="columns is-mobile is-centered">
			<aside className={`column is-1-tablet is-fullheight sesh-sidebar`}>
				<div className="columns is-mobile is-multiline menu-sections is-vcentered is-centered">
					{menu_sections}
				</div>
			</aside>
			{props.children}
			<div className="column is-1" />
		</div>
	);
}

function ResetCalendarFeedsBlurb(props: {
	discord_auth_token: DiscordAuthToken;
	is_admin: boolean;
	guild_id: string;
}) {
	const [is_resetting, setIsResetting] = useState(false);

	const button_classes: string[] = [
		"button dashboard-warning-button",
		"is-size-6",
	];

	if (!props.is_admin) {
		button_classes.push("is-disabled");
	}

	if (is_resetting) {
		button_classes.push("is-loading");
	}

	return (
		<div id="reset_cal_feeds" className="field">
			<div>
				<div className="columns is-mobile is-multiline is-vcentered">
					<div className="column is-narrow">
						{DashboardUtils.CreateHeaderAndInfoButton(
							"Reset ICS Calendar Feeds",
							"Reset server calendar feeds that were generated by /link. Click Reset for more info (you will be asked to confirm)",
							false,
							false,
							false,
							undefined,
							!props.is_admin
						)}
					</div>
					<div className="column">
						<div className="is-pulled-right">
							<button
								className={button_classes.join(" ")}
								disabled={!props.is_admin}
								type="submit"
								onClick={() =>
									DashboardUtils.ShowResetConfirmAlert(
										<p>
											Are you sure you want to reset{" "}
											<span className="has-text-weight-bold">
												ALL
											</span>{" "}
											non-personal calendar .ics feeds for
											this server? Current feeds will no
											longer update, and users will have
											to resubscribe to a new URL
											generated by the{" "}
											<span className="command-example--inline">
												/link
											</span>{" "}
											command in order to continue
											receiving sesh events.
											<br />
											<br />
											<span className="has-text-weight-bold">
												This cannot be undone.
											</span>
										</p>
									).then((val) => {
										if (!val.isConfirmed) {
											return;
										}

										setIsResetting(true);

										const headers =
											common_utils.createSeshHeader(
												props.discord_auth_token
											);

										const params: ResetCalendarFeedsRequest =
											{
												guild_id: props.guild_id,
											};

										fetch(`/api/reset_cal_feeds`, {
											method: "POST",
											headers: headers,
											body: JSON.stringify(params),
										}).then((response) => {
											setIsResetting(false);
											if (response.status === 200) {
												DashboardUtils.ShowSuccessMessageAlert(
													"Calendar Feeds Successfully Deleted",
													""
												);
											} else {
												DashboardUtils.ShowDefaultErrorAlert(
													"Failed to clear server calendar feeds."
												);
											}
											return;
										});
									})
								}
							>
								Reset
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function Party(props: { duration: number }) {
	const [isActive, setIsActive] = useState(true);

	useEffect(() => {
		const timer = setTimeout(() => {
			setIsActive(false);
		}, props.duration);

		return () => {
			clearTimeout(timer);
		};
	});

	const confetti_source_rect = document
		.getElementById("premium-activate-button")
		.getBoundingClientRect();

	var window_width = window.innerWidth;
	var window_height = window.innerHeight;

	return isActive ? (
		<Confetti
			className="confetti"
			width={window_width}
			height={window_height}
			run={isActive}
			confettiSource={{
				x: confetti_source_rect.x + confetti_source_rect.width / 2,
				y: confetti_source_rect.y - 10,
				w: 1,
				h: 1,
			}}
			initialVelocityX={{
				max: 2.5,
				min: -2.5,
			}}
			initialVelocityY={{ max: -2, min: -5 }}
			gravity={0.015}
			recycle={false}
			tweenDuration={100}
			friction={1}
		/>
	) : (
		<></>
	);
}

function UnsavedChangesBanner(props: {
	isVisible: boolean;
	onSave: () => void;
	onRevert: () => void;
	isSaving: boolean;
}) {
	const [isAnimatingOut, setIsAnimatingOut] = useState(false);
	const [isAnimatingIn, setIsAnimatingIn] = useState(false);
	const [shouldRender, setShouldRender] = useState(props.isVisible);

	useEffect(() => {
		if (props.isVisible) {
			setShouldRender(true);
			setIsAnimatingOut(false);
			setIsAnimatingIn(true);
			// Allow initial render then trigger animation
			const timer = setTimeout(() => {
				setIsAnimatingIn(false);
			}, 50);
			return () => clearTimeout(timer);
		} else if (shouldRender) {
			setIsAnimatingOut(true);
			const timer = setTimeout(() => {
				setShouldRender(false);
				setIsAnimatingOut(false);
			}, 300); // Match animation duration
			return () => clearTimeout(timer);
		}
	}, [props.isVisible, shouldRender]);

	if (!shouldRender) {
		return null;
	}

	return (
		<div
			className={`fixed bottom-4 left-4 right-4 z-50 transition-all duration-300 ease-out ${
				isAnimatingOut
					? "translate-y-full opacity-0"
					: isAnimatingIn
					? "translate-y-full opacity-0"
					: "translate-y-0 opacity-100"
			}`}
		>
			<div className="mx-auto max-w-4xl">
				<div className="rounded-lg border border-sesh-dark/20 bg-base-light px-6 py-4 shadow-2xl backdrop-blur-sm">
					<div className="columns is-mobile is-vcentered mb-0">
						<div className="column">
							<div className="flex items-center gap-3">
								<div className="flex h-8 w-8 items-center justify-center rounded-full bg-sesh-primary/10">
									<i className="fas fa-edit text-sm text-sesh-primary"></i>
								</div>
								<div>
									<span className="has-text-weight-semibold text-content-primary">
										You have unsaved changes
									</span>
									<p className="mb-0 text-sm text-content-secondary">
										Don't forget to save your settings
									</p>
								</div>
							</div>
						</div>
						<div className="column is-narrow">
							<div className="flex items-center gap-3">
								<button
									className="text-content-secondary hover:text-content-primary transition-colors duration-200 text-sm font-medium"
									onClick={props.onRevert}
									disabled={props.isSaving}
								>
									Revert
								</button>
								<button
									className={`button dashboard-primary-button--tiny ${
										props.isSaving ? "is-loading" : ""
									}`}
									onClick={props.onSave}
									disabled={props.isSaving}
								>
									<span className="has-text-weight-bold">
										Save Changes
									</span>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
